// src/components/Properties/NodeProperties/NodeProperties.tsx - Simplified without header
import React, { useEffect } from 'react';
import {
  Settings as SettingsIcon,
  Trash2
} from 'lucide-react';
import { useScene } from '@hooks/useScene';
import { useViewItem } from '@hooks/useViewItem';
import { useModelItem } from '@hooks/useModelItem';
import { useUiStateStore } from '@stores/uiStateStore';
import { useAnimation } from 'src/animations/useAnimation';

interface Props {
  id: string;
}

export const NodeProperties: React.FC<Props> = ({ id }) => {
  const { updateModelItem, updateViewItem, deleteViewItem } = useScene();
  const uiStateActions = useUiStateStore((state) => state.actions);
  const animation = useAnimation();

  const viewItem = useViewItem(id);
  const modelItem = useModelItem(id);

  // Subtle pulse animation when node is selected
  useEffect(() => {
    if (viewItem && viewItem.labelHeight !== undefined) {
      animation.pulse(viewItem, 1.05, 400);
    }
  }, [id]); // Trigger when a different node is selected

  if (!viewItem || !modelItem) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <SettingsIcon className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-2 text-sm text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  const handleDelete = () => {
    uiStateActions.setItemControls(null);
    deleteViewItem(viewItem.id);
  };

  return (
    <div className="flex h-full flex-col">
      {/* Properties Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-6">
          {/* Component Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Name
            </label>
            <input
              type="text"
              value={modelItem.name || ''}
              onChange={(e) => {
                updateModelItem(viewItem.id, { name: e.target.value });
              }}
              placeholder="Enter component name"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800 dark:text-white dark:placeholder-gray-500"
            />
          </div>

            {/* Position */}
            {/* <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Position
              </label>
              <div className="mt-1 grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400">X</label>
                  <input
                    type="number"
                    value={viewItem.tile?.x ?? 0}
                    onChange={(e) => {
                      const newX = parseInt(e.target.value) || 0;
                      updateViewItem(viewItem.id, {
                        tile: { 
                          ...viewItem.tile, 
                          x: newX 
                        }
                      });
                    }}
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400">Y</label>
                  <input
                    type="number"
                    value={viewItem.tile?.y ?? 0}
                    onChange={(e) => {
                      const newY = parseInt(e.target.value) || 0;
                      updateViewItem(viewItem.id, {
                        tile: { 
                          ...viewItem.tile, 
                          y: newY 
                        }
                      });
                    }}
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
                  />
                </div>
              </div>
            </div> */}

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Description
              </label>
              <textarea
                value={modelItem.description || ''}
                onChange={(e) => {
                  updateModelItem(viewItem.id, { description: e.target.value });
                }}
                placeholder="Add component description..."
                rows={3}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800 dark:text-white dark:placeholder-gray-500"
              />
            </div>

            {/* ✅ COMBINED: Style Controls in Properties Tab */}
            {/* commented for now.  */}
            {/* <div>
              <ColorPicker
                value={modelItem.color || '#3b82f6'}
                onChange={(color) => updateModelItem(viewItem.id, { color })}
                label="Color"
              />
            </div> */}

            {/* Label Height */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Label Height: {viewItem.labelHeight || 100}px
              </label>
              <input
                type="range"
                min="50"
                max="200"
                step="10"
                value={viewItem.labelHeight || 100}
                onChange={(e) => {
                  updateViewItem(viewItem.id, {
                    labelHeight: parseInt(e.target.value)
                  });
                }}
                className="mt-1 w-full"
              />
            </div>

          {/* Delete Action */}
          <div className="border-t border-gray-200 pt-4 dark:border-slate-600">
            <button
              onClick={handleDelete}
              className="flex items-center gap-2 rounded-md bg-red-50 px-3 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
